export interface MarketingTaxonomyOption {
  id: string;
  label: string;
}

export const CUSTOMER_FUNNEL_STAGES = [
  { id: 'awareness', label: 'Awareness' },
  { id: 'trial', label: 'Trial' },
  { id: 'consideration', label: 'Consideration' },
  { id: 'intent', label: 'Intent' },
  { id: 'purchase', label: 'Purchase' },
  { id: 'loyalty', label: 'Loyalty' },
  { id: 'advocacy', label: 'Advocacy' },
] as const satisfies readonly MarketingTaxonomyOption[];

export type CustomerFunnelStage = (typeof CUSTOMER_FUNNEL_STAGES)[number]['id'];

/**
 * The product's development lifecycle (NPD) — a brand-side timeline distinct
 * from the consumer funnel (`CUSTOMER_FUNNEL_STAGES`). Answers "where is the
 * product in its development?" One example can carry both a product stage and a
 * consumer funnel stage (e.g. teasing a concept to build awareness). Selecting
 * any product stage is the signal that an example is NPD-related.
 */
export const PRODUCT_STAGES = [
  { id: 'idea', label: 'Idea & opportunity' },
  { id: 'concept', label: 'Concept & validation' },
  { id: 'develop', label: 'Development & formulation' },
  { id: 'test', label: 'Testing & sensory' },
  { id: 'pre-launch', label: 'Pre-launch & readiness' },
  { id: 'launch', label: 'Launch' },
  { id: 'post-launch', label: 'Post-launch & iteration' },
] as const satisfies readonly MarketingTaxonomyOption[];

export type ProductStage = (typeof PRODUCT_STAGES)[number]['id'];

export const MARKETING_ROLES = [
  {
    id: 'brand-building',
    label: 'Build demand',
    professionalLabel: 'Brand building',
    description:
      'Create awareness, memory, desire and preference that make future choice more likely.',
  },
  {
    id: 'sales-activation',
    label: 'Activate demand',
    professionalLabel: 'Sales activation',
    description: 'Give people a timely reason and an easy path to try, find or buy now.',
  },
  {
    id: 'retention-advocacy',
    label: 'Compound demand',
    professionalLabel: 'Retention & advocacy',
    description: 'Grow repeat purchase, loyalty and recommendations through existing customers.',
  },
] as const;

export type MarketingRole = (typeof MARKETING_ROLES)[number]['id'];

export const MARKETING_OBJECTIVES = [
  { id: 'get-noticed', label: 'Get noticed' },
  { id: 'create-desire', label: 'Create desire' },
  { id: 'build-preference', label: 'Build preference' },
  { id: 'prompt-action', label: 'Prompt action' },
  { id: 'make-purchase-easy', label: 'Make purchase easy' },
  { id: 'encourage-repeat', label: 'Encourage repeat' },
] as const satisfies readonly MarketingTaxonomyOption[];

export type MarketingObjective = (typeof MARKETING_OBJECTIVES)[number]['id'];

export interface MarketingTacticOption extends MarketingTaxonomyOption {
  description: string;
  journeyStages: readonly CustomerFunnelStage[];
}

export const MARKETING_TACTICS = [
  {
    id: 'product-sampling',
    label: 'Product sampling & tastings',
    description: 'Let people experience the product, believe the promise and buy with confidence.',
    journeyStages: ['awareness', 'trial', 'consideration', 'purchase'],
  },
  {
    id: 'product-demonstration',
    label: 'Product demonstration',
    description: 'Show what the product does in real time to build belief and drive trial.',
    journeyStages: ['trial', 'consideration', 'intent'],
  },
  {
    id: 'retail-display-pos',
    label: 'Retail display & POS',
    description: 'Stand out at the shelf, communicate value and make the product easier to choose.',
    journeyStages: ['awareness', 'consideration', 'intent', 'purchase'],
  },
  {
    id: 'retail-media',
    label: 'Retail media',
    description: 'Reach people while they shop and stay visible close to the buying decision.',
    journeyStages: ['awareness', 'consideration', 'intent', 'purchase'],
  },
  {
    id: 'promotion-offer',
    label: 'Promotion or offer',
    description: 'Give people a timely, relevant reason to try the product or buy now.',
    journeyStages: ['trial', 'intent', 'purchase'],
  },
  {
    id: 'loyalty-programme',
    label: 'Loyalty programme',
    description: 'Reward repeat behaviour and give customers a reason to keep choosing the brand.',
    journeyStages: ['purchase', 'loyalty', 'advocacy'],
  },
  {
    id: 'competition-giveaway',
    label: 'Competition or giveaway',
    description: 'Create excitement, reward engagement and put the product into new hands.',
    journeyStages: ['awareness', 'trial', 'loyalty'],
  },
  {
    id: 'packaging-activation',
    label: 'Packaging activation',
    description: 'Use the pack to attract attention, communicate value and prompt action.',
    journeyStages: ['awareness', 'consideration', 'purchase'],
  },
  {
    id: 'branded-merchandise',
    label: 'Branded merchandise & swag',
    description:
      'Put the brand into everyday life with useful branded items that keep it seen and remembered.',
    journeyStages: ['awareness', 'consideration', 'loyalty', 'advocacy'],
  },
  {
    id: 'collaboration',
    label: 'Collaboration',
    description: 'Borrow reach, relevance and trust by creating something with the right partner.',
    journeyStages: ['awareness', 'consideration', 'intent'],
  },
  {
    id: 'referral-programme',
    label: 'Referral programme',
    description: 'Turn existing customers into a trusted route for introducing new customers.',
    journeyStages: ['awareness', 'trial', 'advocacy'],
  },
  {
    id: 'limited-edition-scarcity',
    label: 'Limited edition or scarcity',
    description: 'Create urgency and renewed interest through constrained availability.',
    journeyStages: ['awareness', 'intent', 'purchase', 'loyalty'],
  },
  {
    id: 'product-launch',
    label: 'Product or flavour launch',
    description: 'Create attention and give new and existing customers a fresh reason to try.',
    journeyStages: ['awareness', 'trial', 'consideration', 'purchase'],
  },
  {
    id: 'content-social-campaign',
    label: 'Content or social campaign',
    description:
      'Inform, entertain or inspire people with content designed to travel and be remembered.',
    journeyStages: ['awareness', 'consideration', 'intent', 'advocacy'],
  },
  {
    id: 'experiential-activation',
    label: 'Experiential activation',
    description: 'Create a participatory brand experience that builds memory, belief and trial.',
    journeyStages: ['awareness', 'trial', 'consideration', 'advocacy'],
  },
  {
    id: 'earned-media-advocacy',
    label: 'Earned media or advocacy',
    description:
      'Build credibility and reach through coverage, recommendations and customer voices.',
    journeyStages: ['awareness', 'consideration', 'advocacy'],
  },
  {
    id: 'live-commerce',
    label: 'Live shopping & live commerce',
    description:
      'Sell in real time through a live video stream, combining demonstration, interaction and an instant path to buy.',
    journeyStages: ['awareness', 'consideration', 'intent', 'purchase'],
  },
] as const satisfies readonly MarketingTacticOption[];

export type MarketingTactic = (typeof MARKETING_TACTICS)[number]['id'];

export const MARKETING_CHANNELS = [
  { id: 'retail-in-store', label: 'Retail & in-store' },
  { id: 'ecommerce-dtc', label: 'E-commerce & DTC' },
  { id: 'social', label: 'Social media' },
  { id: 'email-crm', label: 'Email & CRM' },
  { id: 'consumer-events', label: 'Consumer events' },
  { id: 'hospitality', label: 'Hospitality' },
  { id: 'earned-media', label: 'Earned media' },
  { id: 'brand-owned-experience', label: 'Brand-owned experience' },
  { id: 'everyday-carry', label: 'Everyday carry & wearables' },
] as const satisfies readonly MarketingTaxonomyOption[];

export type MarketingChannel = (typeof MARKETING_CHANNELS)[number]['id'];

export const MARKETING_DELIVERY_DISCIPLINES = [
  { id: 'strategy-insight', label: 'Strategy & insight' },
  { id: 'product-development', label: 'Product development (NPD)' },
  { id: 'creative-design', label: 'Creative & design' },
  { id: 'production', label: 'Production' },
  { id: 'media-placement', label: 'Media & placement' },
  { id: 'activation-staffing', label: 'Activation & staffing' },
  { id: 'logistics-distribution', label: 'Logistics & distribution' },
  { id: 'measurement-optimisation', label: 'Measurement & optimisation' },
] as const satisfies readonly MarketingTaxonomyOption[];

export type MarketingDeliveryDiscipline = (typeof MARKETING_DELIVERY_DISCIPLINES)[number]['id'];

export interface MarketingDeliveryActivityOption extends MarketingTaxonomyOption {
  disciplineId: MarketingDeliveryDiscipline;
}

export const MARKETING_DELIVERY_ACTIVITIES = [
  {
    id: 'shopper-insights-research',
    label: 'Shopper insights & research',
    disciplineId: 'strategy-insight',
  },
  { id: 'campaign-planning', label: 'Campaign planning', disciplineId: 'strategy-insight' },
  {
    id: 'concept-development',
    label: 'Concept & idea development',
    disciplineId: 'product-development',
  },
  {
    id: 'formulation-recipe-development',
    label: 'Formulation & recipe development',
    disciplineId: 'product-development',
  },
  {
    id: 'sensory-taste-testing',
    label: 'Sensory & taste testing',
    disciplineId: 'product-development',
  },
  {
    id: 'product-packaging-development',
    label: 'Product & packaging development',
    disciplineId: 'product-development',
  },
  {
    id: 'pilot-production-scale-up',
    label: 'Pilot & production scale-up',
    disciplineId: 'product-development',
  },
  {
    id: 'compliance-labelling',
    label: 'Compliance & labelling',
    disciplineId: 'product-development',
  },
  { id: 'packaging-design', label: 'Packaging design', disciplineId: 'creative-design' },
  { id: 'point-of-sale-design', label: 'Point-of-sale design', disciplineId: 'creative-design' },
  { id: 'display-design-build', label: 'Display design & build', disciplineId: 'creative-design' },
  {
    id: 'campaign-creative-development',
    label: 'Campaign creative development',
    disciplineId: 'creative-design',
  },
  { id: 'content-production', label: 'Content production', disciplineId: 'production' },
  { id: 'offer-development', label: 'Offer development', disciplineId: 'strategy-insight' },
  {
    id: 'visitor-experience-design',
    label: 'Visitor experience design',
    disciplineId: 'creative-design',
  },
  { id: 'print-production', label: 'Print & production', disciplineId: 'production' },
  {
    id: 'promotional-packaging-design',
    label: 'Promotional packaging design',
    disciplineId: 'production',
  },
  {
    id: 'signage-shelf-material-production',
    label: 'Signage & shelf material production',
    disciplineId: 'production',
  },
  {
    id: 'merchandise-design',
    label: 'Merchandise design & artwork',
    disciplineId: 'creative-design',
  },
  { id: 'merchandise-production', label: 'Merchandise production', disciplineId: 'production' },
  {
    id: 'media-planning-placement',
    label: 'Media planning & placement',
    disciplineId: 'media-placement',
  },
  { id: 'kitting-assembly', label: 'Kitting & assembly', disciplineId: 'logistics-distribution' },
  {
    id: 'warehousing-storage',
    label: 'Warehousing & storage',
    disciplineId: 'logistics-distribution',
  },
  {
    id: 'distribution-delivery',
    label: 'Distribution & delivery',
    disciplineId: 'logistics-distribution',
  },
  {
    id: 'campaign-deployment',
    label: 'Campaign deployment',
    disciplineId: 'logistics-distribution',
  },
  { id: 'asset-management', label: 'Asset management', disciplineId: 'logistics-distribution' },
  {
    id: 'sampling-coordination',
    label: 'Sampling coordination',
    disciplineId: 'activation-staffing',
  },
  { id: 'activation-staffing', label: 'Activation staffing', disciplineId: 'activation-staffing' },
  { id: 'shopper-education', label: 'Shopper education', disciplineId: 'activation-staffing' },
  { id: 'event-setup', label: 'Event setup & bump-in', disciplineId: 'activation-staffing' },
  {
    id: 'campaign-measurement-reporting',
    label: 'Campaign measurement & reporting',
    disciplineId: 'measurement-optimisation',
  },
  { id: 'referral-tracking', label: 'Referral tracking', disciplineId: 'measurement-optimisation' },
] as const satisfies readonly MarketingDeliveryActivityOption[];

export type MarketingDeliveryActivity = (typeof MARKETING_DELIVERY_ACTIVITIES)[number]['id'];

/**
 * How brands worked together to create the demand moment. Answers "who was
 * involved and how were they organised?" — distinct from the `collaboration`
 * tactic (which only says a partnership approach was used). Partners themselves
 * are captured via the content record's linked records, not here.
 */
export const MARKETING_COLLABORATION_MODELS = [
  { id: 'solo', label: 'Solo (single brand)' },
  { id: 'brand-x-brand', label: 'Brand x brand' },
  { id: 'anchor-plus-partners', label: 'Anchor brand + partners' },
  { id: 'collective-multi-brand', label: 'Collective (many brands)' },
  { id: 'host-led', label: 'Host-led (partner venue)' },
  { id: 'creator-collab', label: 'Creator collaboration' },
  { id: 'retailer-collab', label: 'Retailer collaboration' },
] as const satisfies readonly MarketingTaxonomyOption[];

export type MarketingCollaborationModel = (typeof MARKETING_COLLABORATION_MODELS)[number]['id'];

/**
 * The physical, experiential or livestreamed form a demand activation took.
 * Answers "what form did it take?" — distinct from the tactic (the approach).
 * Applies to in-market activations and live events; empty for purely editorial
 * or static digital examples.
 */
export const MARKETING_ACTIVATION_FORMATS = [
  { id: 'pop-up', label: 'Pop-up' },
  { id: 'sampling-station', label: 'Sampling station' },
  { id: 'in-store-demo', label: 'In-store demo' },
  { id: 'roadshow', label: 'Roadshow' },
  { id: 'festival-stall', label: 'Festival stall' },
  { id: 'experiential-installation', label: 'Experiential installation' },
  { id: 'mobile-activation', label: 'Mobile activation' },
  { id: 'shop-in-shop', label: 'Shop-in-shop' },
  { id: 'branded-merchandise', label: 'Branded merchandise' },
  { id: 'branded-packaging', label: 'Branded packaging & reusables' },
  { id: 'live-stream-event', label: 'Live stream event' },
] as const satisfies readonly MarketingTaxonomyOption[];

export type MarketingActivationFormat = (typeof MARKETING_ACTIVATION_FORMATS)[number]['id'];

export interface MarketingJourneyTransition {
  from: CustomerFunnelStage;
  to: CustomerFunnelStage;
}

export interface MarketingAttributes {
  marketingRoles: MarketingRole[];
  journeyStages: CustomerFunnelStage[];
  primaryTransition?: MarketingJourneyTransition;
  /** Product development (NPD) lifecycle stages (optional — most examples have none). */
  productStages?: ProductStage[];
  marketingObjectives: MarketingObjective[];
  tactics: MarketingTactic[];
  channels: MarketingChannel[];
  deliveryDisciplines: MarketingDeliveryDiscipline[];
  deliveryActivities: MarketingDeliveryActivity[];
  /** How brands were organised (optional — many examples have no collaboration). */
  collaborationModels?: MarketingCollaborationModel[];
  /** Physical activation form (optional — only in-market activations have one). */
  activationFormats?: MarketingActivationFormat[];
}

export const MARKETING_TAG_PREFIXES = {
  surface: 'surface',
  marketingRole: 'marketing-role',
  journeyStage: 'journey',
  productStage: 'product-stage',
  marketingObjective: 'objective',
  tactic: 'tactic',
  channel: 'channel',
  deliveryDiscipline: 'delivery-discipline',
  deliveryActivity: 'delivery',
  collaborationModel: 'collab-model',
  activationFormat: 'activation-format',
} as const;

export function buildMarketingTags(attributes: MarketingAttributes): string[] {
  return [
    `${MARKETING_TAG_PREFIXES.surface}:marketing-inspiration`,
    ...attributes.marketingRoles.map((value) => `${MARKETING_TAG_PREFIXES.marketingRole}:${value}`),
    ...attributes.journeyStages.map((value) => `${MARKETING_TAG_PREFIXES.journeyStage}:${value}`),
    ...(attributes.productStages ?? []).map(
      (value) => `${MARKETING_TAG_PREFIXES.productStage}:${value}`
    ),
    ...attributes.marketingObjectives.map(
      (value) => `${MARKETING_TAG_PREFIXES.marketingObjective}:${value}`
    ),
    ...attributes.tactics.map((value) => `${MARKETING_TAG_PREFIXES.tactic}:${value}`),
    ...attributes.channels.map((value) => `${MARKETING_TAG_PREFIXES.channel}:${value}`),
    ...attributes.deliveryDisciplines.map(
      (value) => `${MARKETING_TAG_PREFIXES.deliveryDiscipline}:${value}`
    ),
    ...attributes.deliveryActivities.map(
      (value) => `${MARKETING_TAG_PREFIXES.deliveryActivity}:${value}`
    ),
    ...(attributes.collaborationModels ?? []).map(
      (value) => `${MARKETING_TAG_PREFIXES.collaborationModel}:${value}`
    ),
    ...(attributes.activationFormats ?? []).map(
      (value) => `${MARKETING_TAG_PREFIXES.activationFormat}:${value}`
    ),
  ];
}

export const getCustomerFunnelStageLabel = (stage: CustomerFunnelStage): string =>
  CUSTOMER_FUNNEL_STAGES.find((option) => option.id === stage)?.label ?? stage;

export const getProductStageLabel = (stage: ProductStage): string =>
  PRODUCT_STAGES.find((option) => option.id === stage)?.label ?? stage;

export const CONSUMER_ACTIONS = [
  { id: 'notice', label: 'Notice' },
  { id: 'engage', label: 'Engage' },
  { id: 'save', label: 'Save' },
  { id: 'share', label: 'Share' },
  { id: 'explore', label: 'Explore' },
  { id: 'locate', label: 'Locate' },
  { id: 'enter', label: 'Enter' },
  { id: 'redeem', label: 'Redeem' },
  { id: 'sample', label: 'Sample' },
  { id: 'purchase', label: 'Purchase' },
  { id: 'repurchase', label: 'Repurchase' },
  { id: 'subscribe', label: 'Subscribe' },
  { id: 'review', label: 'Review' },
  { id: 'create-content', label: 'Create content' },
  { id: 'recommend', label: 'Recommend' },
] as const satisfies readonly MarketingTaxonomyOption[];

export type ConsumerAction = (typeof CONSUMER_ACTIONS)[number]['id'];

export interface MarketingJob extends MarketingTaxonomyOption {
  postThemes: MarketingTaxonomyOption[];
  examples: MarketingTaxonomyOption[];
  marketingRole: string;
  funnelStages: CustomerFunnelStage[];
  consumerActions: ConsumerAction[];
}

export interface AudienceAction extends MarketingTaxonomyOption {
  successMeasures: MarketingTaxonomyOption[];
  impact: string;
  funnelStages: CustomerFunnelStage[];
  consumerActions: ConsumerAction[];
}

export const MARKETING_JOBS: MarketingJob[] = [
  {
    id: 'get-noticed',
    label: 'Get noticed',
    postThemes: [
      { id: 'product-discovery', label: 'Product discovery' },
      { id: 'brand-personality-and-entertainment', label: 'Brand personality and entertainment' },
      {
        id: 'collaborations-and-borrowed-audiences',
        label: 'Collaborations and borrowed audiences',
      },
      { id: 'brand-and-business-news', label: 'Brand and business news' },
    ],
    examples: [
      { id: 'new-product-or-flavour-launches', label: 'New product or flavour launches' },
      { id: 'limited-edition-releases', label: 'Limited-edition releases' },
      { id: 'humour-memes-and-trends', label: 'Humour, memes and trend participation' },
      { id: 'brand-or-creator-collaborations', label: 'Brand or creator collaborations' },
      { id: 'milestones-and-announcements', label: 'Milestones and announcements' },
    ],
    marketingRole:
      'Builds reach and mental availability by creating novelty, entertainment and access to relevant new audiences.',
    funnelStages: ['awareness'],
    consumerActions: ['notice', 'engage', 'share'],
  },
  {
    id: 'create-desire',
    label: 'Create desire',
    postThemes: [
      { id: 'usage-and-inspiration', label: 'Usage and inspiration' },
      { id: 'sensory-desire-and-appetite-appeal', label: 'Sensory desire and appetite appeal' },
      { id: 'social-proof-and-popularity', label: 'Social proof and popularity' },
      { id: 'occasion-and-cultural-relevance', label: 'Occasion and cultural relevance' },
    ],
    examples: [
      { id: 'recipes-and-serving-suggestions', label: 'Recipes and serving suggestions' },
      { id: 'pairings-and-usage-ideas', label: 'Pairings and usage ideas' },
      { id: 'sensory-product-moments', label: 'Pouring, cracking, melting or crunching moments' },
      { id: 'reviews-reactions-and-ugc', label: 'Reviews, reactions and user-generated content' },
      { id: 'occasion-based-content', label: 'Seasonal, cultural and occasion-based content' },
    ],
    marketingRole:
      'Creates craving, demonstrates relevant usage occasions and reduces perceived risk through proof that other people enjoy the product.',
    funnelStages: ['awareness', 'consideration'],
    consumerActions: ['engage', 'save', 'share', 'explore'],
  },
  {
    id: 'build-preference',
    label: 'Build preference',
    postThemes: [
      { id: 'product-differentiation', label: 'Product differentiation' },
      { id: 'values-provenance-and-trust', label: 'Values, provenance and trust' },
      { id: 'education-and-problem-solving', label: 'Education and problem-solving' },
    ],
    examples: [
      { id: 'ingredients-and-formulation', label: 'Ingredient and formulation stories' },
      { id: 'features-and-differentiators', label: 'Product features and differentiators' },
      { id: 'local-and-ethical-sourcing', label: 'Local and ethical sourcing' },
      { id: 'quality-and-certifications', label: 'Quality standards and certifications' },
      {
        id: 'category-education-and-faqs',
        label: 'Category education and frequently asked questions',
      },
    ],
    marketingRole:
      'Gives people rational and emotional reasons to choose the brand over alternatives while removing doubts or objections.',
    funnelStages: ['consideration', 'intent'],
    consumerActions: ['save', 'explore'],
  },
  {
    id: 'prompt-action',
    label: 'Prompt action',
    postThemes: [
      { id: 'promotions-and-purchase-triggers', label: 'Promotions and purchase triggers' },
      {
        id: 'competitions-and-community-participation',
        label: 'Competitions and community participation',
      },
      { id: 'sampling-and-trial', label: 'Sampling and trial' },
      { id: 'scarcity-and-urgency', label: 'Scarcity and urgency' },
    ],
    examples: [
      { id: 'discounts-coupons-and-bundles', label: 'Discounts, coupons and bundles' },
      { id: 'giveaways-and-competitions', label: 'Giveaways and competitions' },
      { id: 'free-samples-and-tastings', label: 'Free samples and tastings' },
      { id: 'limited-time-offers', label: 'Limited-time offers' },
      { id: 'voting-and-submissions', label: 'Voting, challenges and submissions' },
    ],
    marketingRole:
      'Converts existing awareness and desire into a measurable next step by adding incentive, urgency or a low-risk way to try.',
    funnelStages: ['intent', 'trial', 'purchase'],
    consumerActions: ['engage', 'enter', 'redeem', 'sample', 'purchase'],
  },
  {
    id: 'make-purchase-easy',
    label: 'Make purchase easy',
    postThemes: [
      { id: 'availability-and-path-to-purchase', label: 'Availability and path to purchase' },
      { id: 'retail-and-hospitality-activation', label: 'Retail and hospitality activation' },
    ],
    examples: [
      { id: 'new-retailer-listings', label: 'New retailer listings' },
      {
        id: 'store-locator-and-shelf-guidance',
        label: 'Store locator and shelf-location guidance',
      },
      { id: 'online-purchase-and-delivery', label: 'Online purchase and delivery links' },
      { id: 'restocks-and-regional-expansion', label: 'Restocks and regional expansion' },
      { id: 'menu-and-in-store-placements', label: 'Menu and in-store placements' },
    ],
    marketingRole:
      'Reduces purchase friction by clearly showing people where, when and how to buy the product.',
    funnelStages: ['intent', 'purchase'],
    consumerActions: ['locate', 'purchase'],
  },
  {
    id: 'encourage-repeat',
    label: 'Encourage repeat',
    postThemes: [
      { id: 'retention-and-repeat-purchase', label: 'Retention and repeat purchase' },
      { id: 'range-discovery', label: 'Range discovery' },
      { id: 'new-usage-occasions', label: 'New usage occasions' },
      { id: 'loyalty-and-community', label: 'Loyalty and community' },
    ],
    examples: [
      { id: 'new-ways-to-use-a-favourite', label: 'New ways to use a familiar product' },
      { id: 'range-and-flavour-discovery', label: 'Range and flavour discovery' },
      { id: 'replenishment-reminders', label: 'Replenishment reminders' },
      { id: 'loyalty-and-subscription-offers', label: 'Loyalty and subscription offers' },
      { id: 'customer-exclusives-and-early-access', label: 'Customer exclusives and early access' },
    ],
    marketingRole:
      'Increases purchase frequency and lifetime value by creating more reasons to consume, replenish and explore the range.',
    funnelStages: ['loyalty', 'advocacy'],
    consumerActions: ['repurchase', 'subscribe', 'review', 'create-content', 'recommend'],
  },
];

export const AUDIENCE_ACTIONS: AudienceAction[] = [
  {
    id: 'notice-and-remember',
    label: 'Notice and remember the brand',
    successMeasures: [
      { id: 'unique-reach', label: 'Unique reach' },
      { id: 'video-view-through-rate', label: 'Video view-through rate' },
      { id: 'brand-search-lift', label: 'Brand search lift' },
      { id: 'aided-awareness-lift', label: 'Aided awareness lift' },
    ],
    impact: 'Expands the pool of people who can recall the brand in a relevant buying situation.',
    funnelStages: ['awareness'],
    consumerActions: ['notice'],
  },
  {
    id: 'engage-with-content',
    label: 'Engage with the content',
    successMeasures: [
      { id: 'engagement-rate', label: 'Engagement rate' },
      { id: 'comments-and-replies', label: 'Comments and replies' },
      { id: 'poll-or-vote-completion-rate', label: 'Poll or vote completion rate' },
    ],
    impact:
      'Signals relevance and can increase organic distribution and future content efficiency.',
    funnelStages: ['awareness', 'consideration'],
    consumerActions: ['engage'],
  },
  {
    id: 'save-for-later',
    label: 'Save for later',
    successMeasures: [
      { id: 'saves', label: 'Saves' },
      { id: 'save-rate', label: 'Saves as a percentage of reach' },
      { id: 'return-visits', label: 'Return visits to the content or linked page' },
    ],
    impact:
      'Keeps recipes, usage ideas and product information available for a later purchase or consumption occasion.',
    funnelStages: ['consideration', 'intent'],
    consumerActions: ['save'],
  },
  {
    id: 'share-or-recommend',
    label: 'Share or recommend the brand',
    successMeasures: [
      { id: 'shares-and-sends', label: 'Shares and direct sends' },
      { id: 'earned-reach', label: 'Earned reach' },
      { id: 'referral-traffic', label: 'Referral traffic' },
      { id: 'positive-mentions', label: 'Positive brand mentions' },
    ],
    impact: 'Adds trusted word-of-mouth and introduces the brand to relevant new audiences.',
    funnelStages: ['awareness', 'advocacy'],
    consumerActions: ['share', 'recommend'],
  },
  {
    id: 'explore-the-product',
    label: 'Explore the product or range',
    successMeasures: [
      { id: 'product-page-click-through-rate', label: 'Product-page click-through rate' },
      { id: 'product-page-engagement', label: 'Product-page engaged sessions' },
      { id: 'range-page-views', label: 'Range-page views' },
    ],
    impact: 'Moves people from passive awareness into active consideration and range discovery.',
    funnelStages: ['consideration'],
    consumerActions: ['explore'],
  },
  {
    id: 'find-where-to-buy',
    label: 'Find where to buy',
    successMeasures: [
      { id: 'store-locator-uses', label: 'Store-locator uses' },
      { id: 'retailer-link-clicks', label: 'Retailer-link clicks' },
      { id: 'directions-or-location-clicks', label: 'Directions or location clicks' },
    ],
    impact: 'Removes availability uncertainty and shortens the path from intent to purchase.',
    funnelStages: ['intent'],
    consumerActions: ['locate'],
  },
  {
    id: 'claim-an-offer-or-enter',
    label: 'Claim an offer or enter a competition',
    successMeasures: [
      { id: 'offer-redemptions', label: 'Offer or coupon redemptions' },
      { id: 'competition-entries', label: 'Competition entries' },
      { id: 'cost-per-action', label: 'Cost per redemption or entry' },
    ],
    impact:
      'Turns attention into an attributable action and creates a route for follow-up where permission is given.',
    funnelStages: ['intent', 'trial'],
    consumerActions: ['enter', 'redeem'],
  },
  {
    id: 'try-or-sample',
    label: 'Try or sample the product',
    successMeasures: [
      { id: 'samples-distributed', label: 'Qualified samples distributed' },
      { id: 'sample-claim-rate', label: 'Sample claim rate' },
      { id: 'trial-to-purchase-conversion', label: 'Trial-to-purchase conversion rate' },
      { id: 'cost-per-trial', label: 'Cost per trial' },
    ],
    impact:
      'Reduces trial risk and produces direct evidence of whether product experience converts into demand.',
    funnelStages: ['trial'],
    consumerActions: ['sample'],
  },
  {
    id: 'purchase',
    label: 'Purchase the product',
    successMeasures: [
      { id: 'attributed-sales', label: 'Attributed sales or revenue' },
      { id: 'purchase-conversion-rate', label: 'Purchase conversion rate' },
      { id: 'retailer-sell-through', label: 'Retailer sell-through' },
      { id: 'customer-acquisition-cost', label: 'Customer acquisition cost' },
    ],
    impact:
      'Converts generated demand into revenue and validates whether content reaches commercially valuable audiences.',
    funnelStages: ['purchase'],
    consumerActions: ['purchase'],
  },
  {
    id: 'buy-again',
    label: 'Buy again',
    successMeasures: [
      { id: 'repeat-purchase-rate', label: 'Repeat-purchase rate' },
      { id: 'time-to-second-purchase', label: 'Time to second purchase' },
      { id: 'purchase-frequency', label: 'Purchase frequency' },
      { id: 'subscription-or-loyalty-uptake', label: 'Subscription or loyalty uptake' },
    ],
    impact: 'Builds habitual demand, improves retention and increases customer lifetime value.',
    funnelStages: ['loyalty'],
    consumerActions: ['repurchase', 'subscribe'],
  },
  {
    id: 'create-ugc-or-review',
    label: 'Create content or leave a review',
    successMeasures: [
      { id: 'ugc-submissions', label: 'User-generated content submissions' },
      { id: 'review-volume', label: 'New review volume' },
      { id: 'average-rating', label: 'Average rating' },
      { id: 'ugc-reuse-rate', label: 'Percentage of submissions suitable for reuse' },
    ],
    impact:
      'Creates reusable social proof that can improve trust and conversion for future audiences.',
    funnelStages: ['loyalty', 'advocacy'],
    consumerActions: ['review', 'create-content', 'recommend'],
  },
];
